/**
 * @solum/rules — the client-side twin of the core rule grammar.
 *
 * ONE shared implementation of the 0026 condition evaluator + 0027 firing, so
 * the console (and any other JS surface) evaluates rules identically instead of
 * re-deriving the grammar inline, page by page. ENFORCEMENT STAYS SERVER-SIDE
 * (core.fire_rules over RLS is the source of truth); this is UX truth — the same
 * verdict, shown before the round-trip (a stage guard blocking a move, a form
 * warning). Keep this in lock-step with packages/core/migrations/0026_expression
 * + 0027_rules; the tests pin the operator semantics.
 *
 * Covers BOTH halves of the grammar: the CONDITION evaluator (evalLeaf — 0026)
 * and the NUMERIC evaluator (evalNumber — pricing/computed values, 0028). Keep
 * both in lock-step with the SQL twins; the tests pin the semantics.
 */

/** A condition node: a leaf (field/op/value) or a composite (all/any/not). */
export interface Leaf {
  field?: string
  op?: string
  value?: unknown
  all?: Leaf[]
  any?: Leaf[]
  not?: Leaf
  /**
   * A registered SQL check, run by `core.evaluate_predicate`
   * (`2026080921:240-241`). Part of the grammar since that migration, and the
   * entire condition of six rules `moonbase-onboarding` ships — so a client
   * that does not know the node reads those rules as unconditional. This twin
   * cannot EVALUATE one (the check is SQL over live data), and `evalLeaf`
   * deliberately does not try; it is here so a reader can be TOLD.
   */
  predicate?: string
}
export type Binds = Record<string, unknown>
export type Ctx = Record<string, unknown>

/** Resolve a possibly-dotted path (a.b.c) against the context — mirrors the SQL
 *  evaluator's #>> path extraction; flat fields are the common case. */
function pluck(row: Ctx, field: string): unknown {
  if (!field.includes('.')) return row[field]
  return field.split('.').reduce<unknown>((o, k) => (o == null ? undefined : (o as Ctx)[k]), row)
}

/** Substitute a $-bind (e.g. $me, $record) from the binds map; literals pass through. */
function resolve(value: unknown, binds: Binds): unknown {
  return typeof value === 'string' && value.startsWith('$') && value in binds ? binds[value] : value
}

/** Evaluate a condition node against a context row. Fail-safe: a missing field
 *  compares false, never throws; an empty node (no field/op) always matches. */
export function evalLeaf(row: Ctx, f: Leaf, binds: Binds = {}): boolean {
  if (f.any) return f.any.some((x) => evalLeaf(row, x, binds))
  if (f.all) return f.all.every((x) => evalLeaf(row, x, binds))
  if (f.not) return !evalLeaf(row, f.not, binds)
  if (!f.field || !f.op) return true
  const v = pluck(row, f.field)
  const val = resolve(f.value, binds)
  switch (f.op) {
    case 'in': return Array.isArray(val) && (val as unknown[]).includes(v)
    case 'eq': return String(v).toLowerCase() === String(val).toLowerCase()
    case 'ne': return String(v).toLowerCase() !== String(val).toLowerCase()
    case 'gt': return Number(v) > Number(val)
    case 'gte': return Number(v) >= Number(val)
    case 'lt': return Number(v) < Number(val)
    case 'lte': return Number(v) <= Number(val)
    case 'contains': return String(v ?? '').toLowerCase().includes(String(val).toLowerCase())
    // The anchored siblings of `contains`, added with the column ⋮ filter menu
    // (#959). Deliberately the SAME case-insensitive reading, because the
    // PostgREST twin is `ilike.needle*` / `ilike.*needle` — one substring family
    // with one meaning, not three that happen to look alike.
    case 'starts': return String(v ?? '').toLowerCase().startsWith(String(val).toLowerCase())
    case 'ends': return String(v ?? '').toLowerCase().endsWith(String(val).toLowerCase())
    case 'after': return String(v) > String(val)
    case 'before': return String(v) < String(val)
    case 'empty': return v == null || v === ''
    default: return true
  }
}

/**
 * The WORDS for the grammar's operators — one map, where the grammar is.
 *
 * These moved here from `packages/ui/src/grid/columnFilter.ts`, which had them
 * as `GENERIC_OPS` for describing a filter chip. Explaining a rule needs the
 * same thirteen readings, and a second copy of a vocabulary is a fork that
 * agrees until somebody adds an operator to one of them. The grid still uses a
 * FIELD's own offered label where it has one — a date reads “on” and a number
 * reads “=” — and falls back to these; this is the generic reading, so it is
 * the one the grammar owns.
 *
 * The set is pinned to the evaluator's own `case` arms by a test that reads
 * `evalLeaf`'s source. An operator `evalLeaf` handles and this map does not is
 * one a person would see written as its bare code.
 */
export const OP_WORDS: Record<string, string> = {
  contains: 'contains', eq: 'is', ne: 'is not', starts: 'starts with', ends: 'ends with',
  in: 'is any of', gt: '>', gte: '≥', lt: '<', lte: '≤',
  after: 'after', before: 'before', empty: 'is empty',
  // Two the SERVER runs and this twin does not — see `RULE_OPS`. A condition
  // already carrying one has to be readable even though `evalLeaf` cannot
  // evaluate it, because a reader meets the data before they meet the fork.
  exists: 'has a value', is_null: 'is not set',
}

/**
 * What a RULE may be authored with — the operators the SERVER runs.
 *
 * This is not `evalLeaf`'s set, and the difference is the point. A rule's
 * condition is evaluated by `core.evaluate_condition` → `core.evaluate_expression`
 * (live at `0123_expression_missing_field_is_false.sql:38-76`), whose `case`
 * ends `else false`: an operator it does not know makes the rule **silently
 * never match**. This twin evaluates five more (`starts`, `ends`, `after`,
 * `before`, `empty`) and lacks two the server has (`exists`, `is_null`).
 *
 * So an editor that offered `evalLeaf`'s set would offer five ways to write a
 * rule that can never fire, in a dropdown, with Explain mode reading each one
 * back in confident prose. Offering is a promise about the SERVER; evaluating
 * locally is a preview. `grammarWords.seam.test.ts` pins this list to the
 * migration's own `when '<op>'` arms, so it cannot drift from the thing it
 * describes — which is what `packages/ui`'s copy of the words never did.
 */
export const RULE_OPS: readonly string[] = [
  'eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'in', 'contains', 'exists', 'is_null',
]

/** How a value reads in a sentence. Arrays are the `in` case; `$binds` are named. */
function showValue(v: unknown): string {
  if (Array.isArray(v)) return v.map(showValue).join(', ')
  if (v == null || v === '') return ''
  const s = String(v)
  // A `$bind` is a name, not a literal: `$me` reads as “me”, not as “$me”.
  return s.startsWith('$') ? s.slice(1) : s
}

export interface ExplainOptions {
  /** Column → the label a person reads. Defaults to the column name. */
  label?: (field: string) => string
}

/**
 * A condition, in plain language (#1736 — “Explain mode”).
 *
 * `“When X, if Y, move from A to B…”` is only honest if the Y is DERIVED from
 * the same JSON the evaluator reads, so this walks the grammar rather than
 * restating it. Rule 42: a hand-written summary beside a machine-read condition
 * is two statements that will disagree, and the one a person believes is the
 * prose.
 *
 * An EMPTY node reads “always”, and that is the important case rather than an
 * edge one: `core.flow_transition.condition` is `jsonb not null default '{}'`,
 * so most real transitions arrive as `{}` and mean exactly that.
 */
export function explainCondition(leaf: Leaf | null | undefined, opts: ExplainOptions = {}): string {
  const name = opts.label ?? ((f: string) => f)

  const walk = (f: Leaf, depth: number): string => {
    /* The SERVER's precedence, in the server's order: predicate, all, any, not,
       leaf (`core.evaluate_condition`, `2026080921:239-259`). Not this file's
       own preference, and not `evalLeaf`'s either — `evalLeaf` checks `any`
       first, so a node carrying two keys is read differently by the two of them
       and only one can be right about what will happen. */
    if (f.predicate) {
      // A registered predicate is a named SQL check the server runs
      // (`core.evaluate_predicate`). Its name is all a browser may say about
      // it; running it here would be a second implementation of a gate.
      return `the check ${f.predicate} passes`
    }
    if (f.all) {
      // A clause that is always true adds nothing to an AND.
      const said = f.all.map((p) => walk(p, depth + 1)).filter((x) => x !== 'always')
      if (said.length === 0) return 'always'
      return said.length === 1 ? said[0]! : bracket(said.join(' and '), depth)
    }
    if (f.any) {
      /* An OR is the mirror image, and the first version of this got it
         backwards: a clause that is always true makes the WHOLE disjunction
         always true, so dropping it turns an unconditional rule into a
         conditional-looking sentence. That is the shape a half-authored leaf
         produces — `ConditionBuilder`'s “add row” emits `{field:'',op:'eq'}`,
         which is `always` — and it is the reading a reviewer is most likely to
         sign off on. An EMPTY `any` matches nothing, which both evaluators
         agree on and neither reads as “always”. */
      const said = f.any.map((p) => walk(p, depth + 1))
      if (said.length === 0) return 'never'
      if (said.some((x) => x === 'always')) return 'always'
      return said.length === 1 ? said[0]! : bracket(said.join(' or '), depth)
    }
    if (f.not) return `not ${walk(f.not, depth + 1)}`
    if (!f.field || !f.op) return 'always'
    const word = OP_WORDS[f.op] ?? f.op
    // The two operators that take no value — saying “is empty nothing” is worse
    // than saying nothing at all.
    if (f.op === 'empty' || f.op === 'exists' || f.op === 'is_null') return `${name(f.field)} ${word}`
    const value = showValue(f.value)
    return value === '' ? `${name(f.field)} ${word} nothing` : `${name(f.field)} ${word} ${value}`
  }

  /** Brackets only where the nesting could be misread — not at the top level. */
  const bracket = (joined: string, depth: number) => (depth === 0 ? joined : `(${joined})`)

  return walk(leaf ?? {}, 0)
}

/**
 * A rule's matched action, in the shape `core.fire_rules` builds it
 * (`0027_rules.sql:104-107`): the rule's identity, its module-interpreted
 * `action_type`, the payload the module reads, the severity, and the rendered
 * `message_template`.
 *
 * This used to be `{ type, message }`, which is a shape the SERVER NEVER
 * RETURNS. `core.rule` has no `action` column — it has `action_type`,
 * `action_payload` and `message_template` (`0027_rules.sql:22-40`) — so
 * `r.action` was `undefined` for every row a real deployment holds, and the
 * console's stage guard read `.type` off that `undefined` the moment any rule
 * MATCHED. It worked only against the fixture, which is the only shape the
 * browser suite runs. Fixed by taking the server's own vocabulary rather than
 * inventing a client one for it to be translated into.
 */
export interface RuleAction {
  action_type: string
  action_payload?: Record<string, unknown>
  severity?: string
  message?: string | null
  rule_id?: string
  module?: string
  code?: string
  [k: string]: unknown
}

/** A rule row as `core.rule` stores it — the columns, not a client's idea of them. */
export interface RuleRow {
  id?: string
  module?: string
  code?: string
  is_active?: boolean
  priority?: number | null
  severity?: string
  condition?: Leaf
  action_type?: string
  action_payload?: Record<string, unknown>
  message_template?: string | null
}

/** The action a matched row produces — the same projection `fire_rules` makes. */
function actionOf(r: RuleRow): RuleAction {
  return {
    rule_id: r.id, module: r.module, code: r.code,
    action_type: r.action_type ?? '',
    action_payload: r.action_payload ?? {},
    severity: r.severity ?? 'info',
    message: r.message_template ?? null,
  }
}

/** Fire the active rules for a context in priority order → their matched actions.
 *  The caller interprets the actions (e.g. a `block` refuses a stage move). */
export function fireRules(rules: RuleRow[], ctx: Ctx, binds: Binds = {}): RuleAction[] {
  return rules
    .filter((r) => r.is_active !== false)
    .sort((a, b) => Number(a.priority ?? 0) - Number(b.priority ?? 0))
    .filter((r) => evalLeaf(ctx, (r.condition ?? {}) as Leaf, binds))
    .map(actionOf)
}

/**
 * The numeric half of the grammar — the twin of core.eval_number (0028). A small,
 * safe expression tree for pricing/computed values; pure + fail-safe (missing
 * field → 0, divide-by-zero → 0, unrecognised node → 0). Grammar:
 *   5                                    numeric literal
 *   { field: "line.qty" }                dotted context path (non-numeric → 0)
 *   { "+": [..] } { "*": [..] }          n-ary sum / product
 *   { "-": [a,b] } { "/": [a,b] }        binary; b=0 → 0
 *   { min: [..] } { max: [..] }
 *   { round: [e] } | { round: [e, n] }   n decimals (default 0)
 *   { abs: [e] }
 *   { if: [<condition>, <then>, <else>] } condition via evalLeaf (tiered pricing)
 */
export type NumExpr = number | { [op: string]: unknown } | null | undefined
export function evalNumber(expr: NumExpr, ctx: Ctx, binds: Binds = {}): number {
  if (expr == null) return 0
  if (typeof expr === 'number') return expr
  if (typeof expr !== 'object') return 0
  const e = expr as Record<string, unknown>
  if ('field' in e) {
    const n = Number(pluck(ctx, String(e.field)))
    return Number.isFinite(n) ? n : 0
  }
  const n = (x: unknown) => evalNumber(x as NumExpr, ctx, binds)
  const arr = (x: unknown): unknown[] => (Array.isArray(x) ? x : [])
  if ('if' in e) { const a = arr(e.if); return evalLeaf(ctx, (a[0] ?? {}) as Leaf, binds) ? n(a[1]) : n(a[2] ?? 0) }
  if ('+' in e) return arr(e['+']).reduce<number>((s, x) => s + n(x), 0)
  if ('*' in e) return arr(e['*']).reduce<number>((s, x) => s * n(x), 1)
  if ('-' in e) { const a = arr(e['-']); return n(a[0]) - n(a[1]) }
  if ('/' in e) { const a = arr(e['/']); const d = n(a[1]); return d === 0 ? 0 : n(a[0]) / d }
  if ('min' in e) { const xs = arr(e.min).map(n); return xs.length ? Math.min(...xs) : 0 }
  if ('max' in e) { const xs = arr(e.max).map(n); return xs.length ? Math.max(...xs) : 0 }
  if ('round' in e) { const a = arr(e.round); const f = 10 ** Number(a[1] ?? 0); return Math.round(n(a[0]) * f) / f }
  if ('abs' in e) return Math.abs(n(arr(e.abs)[0]))
  return 0
}
